/**
 * O PDF na tela, em qualquer aparelho.
 *
 * O `<iframe>` entrega o leitor do navegador — busca, zoom, imprimir —, e é o
 * melhor que existe onde ele existe. Mas não existe em todo lugar: o Chrome do
 * Android não tem leitor embutido (o quadro fica em branco, ou oferece baixar),
 * e `navigator.pdfViewerEnabled` diz isso com todas as letras. Metade do
 * escritório trabalha no tablet; PDF em branco lá é documento que ninguém lê.
 *
 * Sem leitor nativo, as páginas são desenhadas em `<canvas>` pelo pdf.js que o
 * servidor já usa (`unpdf`, o mesmo de `src/pdf.ts`). O módulo tem 1,6 MB e só
 * desce quando esse ramo acontece — no computador ele nunca é pedido.
 *
 * Nos dois ramos o quadro diz que está carregando (era um retângulo branco
 * mudo até o PDF chegar) e, se falhar, diz o que fazer em vez de ficar vazio.
 */
import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/ui/spinner";
import { Icone, ICONES } from "../visual.jsx";

/* Nulo em ambiente sem `navigator` (teste): o ramo nativo é o padrão seguro. */
export const temLeitorNativo = () => typeof navigator === "undefined" || navigator.pdfViewerEnabled !== false;

/* Documento de processo passa de cem páginas; desenhar todas de uma vez trava o
   tablet. Acima disto a última folha diz quantas faltam e oferece o arquivo. */
const TETO_DE_PAGINAS = 40;

function Falha({ url, nome }) {
  return (
    <div className="flex size-full flex-col items-center justify-center gap-3 p-6 text-center text-foreground">
      <Icone de={ICONES.documento} tamanho={30} cor="var(--color-tinta-fantasma)" />
      <div className="text-corpo font-semibold [overflow-wrap:anywhere]">{nome}</div>
      <div className="max-w-[360px] text-corpo leading-[1.5] text-tinta-suave">
        Não deu para mostrar este PDF aqui. Abra numa aba nova ou baixe para ler no aparelho.
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <a
          href={url}
          target="_blank"
          rel="noopener"
          className="rounded-[10px] border border-foreground/14 px-3 py-2 text-corpo font-semibold text-tinta-media no-underline"
        >
          Abrir em outra aba
        </a>
        <a
          href={url}
          download={nome}
          className="rounded-[10px] bg-foreground px-3 py-2 text-corpo font-semibold text-creme no-underline"
        >
          Baixar
        </a>
      </div>
    </div>
  );
}

/** As páginas desenhadas pelo pdf.js — o ramo sem leitor nativo. */
function Paginas({ url, nome, primeira }) {
  const caixa = useRef(null);
  const [estado, setEstado] = useState("carregando");
  const [sobra, setSobra] = useState(0);

  useEffect(() => {
    let vivo = true;
    let doc = null;
    const alvo = caixa.current;
    (async () => {
      try {
        const [{ getDocumentProxy }, resposta] = await Promise.all([import("unpdf"), fetch(url, { credentials: "same-origin" })]);
        if (!resposta.ok) throw new Error(String(resposta.status));
        doc = await getDocumentProxy(new Uint8Array(await resposta.arrayBuffer()));
        if (!vivo || !alvo) return;
        const total = primeira ? 1 : Math.min(doc.numPages, TETO_DE_PAGINAS);
        /* A largura é a do quadro, e a densidade é a da tela: canvas em 1x num
           telefone de 3x é texto borrado, que num documento é o mesmo que não ter. */
        const largura = alvo.clientWidth || 800;
        const densidade = Math.min(window.devicePixelRatio || 1, 2);
        for (let n = 1; n <= total; n++) {
          // eslint-disable-next-line no-await-in-loop -- página a página, na ordem: a primeira aparece antes de a última ser lida
          const pagina = await doc.getPage(n);
          if (!vivo) return;
          const base = pagina.getViewport({ scale: 1 });
          const escala = largura / base.width;
          const vista = pagina.getViewport({ scale: escala * densidade });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(vista.width);
          canvas.height = Math.floor(vista.height);
          canvas.style.width = "100%";
          canvas.setAttribute("aria-label", `Página ${n} de ${doc.numPages}`);
          canvas.className = primeira ? "block bg-card" : "mb-2 block rounded-[4px] bg-card shadow-sm";
          // eslint-disable-next-line no-await-in-loop -- idem
          await pagina.render({ canvasContext: canvas.getContext("2d"), viewport: vista }).promise;
          if (!vivo) return;
          alvo.appendChild(canvas);
          if (n === 1) setEstado("pronto");
        }
        if (!primeira && doc.numPages > total) setSobra(doc.numPages - total);
      } catch {
        if (vivo) setEstado("falhou");
      }
    })();
    return () => {
      vivo = false;
      /* Solta o documento na troca de arquivo: o visor anda de PDF em PDF, e sem
         isto cada um ficava na memória do tablet. Opcional e calado — a limpeza
         que lança derruba a árvore inteira do React junto (foi o que aconteceu). */
      try {
        void (doc?.loadingTask ?? doc)?.destroy?.();
      } catch {
        /* nada a fazer: o quadro já está saindo */
      }
      alvo?.replaceChildren();
    };
  }, [url, primeira]);

  if (estado === "falhou") return <Falha url={url} nome={nome} />;

  return (
    /* Fundo de mesa atrás das folhas: página branca sobre o mesmo creme não tem
       borda, e o fim da última folha se perdia no quadro. */
    <div className={`relative size-full ${primeira ? "overflow-hidden" : "overflow-y-auto bg-muted p-2"}`}>
      <div ref={caixa} />
      {estado === "carregando" ? <Carregando /> : null}
      {sobra ? (
        <div className="p-3 text-center text-corpo text-tinta-suave">
          Mais {sobra} página(s).{" "}
          <a href={url} download={nome} className="font-semibold text-foreground">
            Baixe o PDF
          </a>{" "}
          para ler inteiro.
        </div>
      ) : null}
    </div>
  );
}

function Carregando() {
  return (
    <div role="status" className="absolute inset-0 flex items-center justify-center gap-2 text-corpo text-muted-foreground">
      <Spinner /> Abrindo o PDF…
    </div>
  );
}

/**
 * `primeira` é a prévia: uma página só, inteira no quadro, sem rolagem — é para
 * reconhecer o documento, e ler é no visor.
 * `parametros` vai no `#` do leitor nativo (`toolbar=0&view=FitH`…).
 */
export default function LeitorDePdf({ url, nome, primeira = false, parametros = "", className = "" }) {
  /* Guarda QUAL endereço carregou, não um booleano: a seta do visor troca o
     arquivo no mesmo quadro, e um "carregou" herdado do PDF anterior tirava o
     aviso de abrindo do seguinte. */
  const [carregado, setCarregado] = useState(null);
  const [nativo] = useState(temLeitorNativo);

  return (
    <div className={`relative overflow-hidden bg-card ${className}`}>
      {nativo ? (
        <>
          <iframe
            key={url}
            src={parametros ? `${url}#${parametros}` : url}
            title={nome}
            onLoad={() => setCarregado(url)}
            tabIndex={primeira ? -1 : undefined}
            className={`block size-full border-none bg-card ${primeira ? "pointer-events-none" : ""}`}
          />
          {carregado === url ? null : <Carregando />}
        </>
      ) : (
        <Paginas key={url} url={url} nome={nome} primeira={primeira} />
      )}
    </div>
  );
}
